<script setup lang="ts">
import { ref, computed, toRefs, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import icons from './icons';
import { getContent } from './templates';
import { AksInsertCtaButton, PLUGIN_KEY } from './config';
import { EditorInteractionManager } from '../types';
import LinkEditorModal, { ModalMode } from '../shared/link-editor-modal/link-editor-modal.vue';
import { CaptureErrorFunc } from '../../use-error-logger';

defineOptions({ name: 'AksInsertCtaPlugin' });

const props = defineProps<{
  id: string;
  editorInteractionManager: EditorInteractionManager;
  captureError: CaptureErrorFunc;
}>();

const emit = defineEmits(['toolbarButtonClicked', 'contentInserted'])

const { id, editorInteractionManager: im } = toRefs(props);
const modal = ref<typeof LinkEditorModal>(null);
const { t } = useI18n();

const selectedNode = ref<Element | null>(null);
const modalMode = ref<ModalMode>(ModalMode.Closed);

const pluginId = computed(() => id.value + '_' + PLUGIN_KEY);

const register = () => {
  im.value.registerPlugin(pluginId.value, ({ addButton, addContextToolbar, addContextMenu, addIcons, on }) => {
      addIcons(icons);

      addButton(AksInsertCtaButton.InsertCta, {
        text: t('account.brands.createCampaigns.editor.toolbar.insertCta.label'),
        icon: 'touch-long',
        onAction: () => {
          openModal({ mode: ModalMode.Insert });
          emit('toolbarButtonClicked', PLUGIN_KEY);
        }
      });

      addButton(AksInsertCtaButton.EditCta, {
        icon: 'edit-block',
        onAction: () => {
          if (selectedNode.value) {
            openModal({
              mode: ModalMode.Edit,
              label: selectedNode.value?.textContent?.trim(),
              url: selectedNode.value?.getAttribute('href'),
            });
          }
        }
      });

      addButton(AksInsertCtaButton.DeleteCta, {
        icon: 'trash',
        onAction: () => {
          if (selectedNode.value) {
            im.value.selectNode(selectedNode.value);
            im.value.deleteNodeContent();
          }
        }
      });

      addContextToolbar('textselection', {
        predicate: (node) => {
          if (node?.hasAttribute(`data-${PLUGIN_KEY}`)) {
            selectedNode.value = node;
            im.value.selectNode(selectedNode.value);
            return true;
          }

          return false;
        },
        items: [AksInsertCtaButton.EditCta, AksInsertCtaButton.DeleteCta].join(' '),
        position: 'node',
        scope: 'node'
      });

      // Disable standard link editor context menu for CTA links
      addContextMenu('link', {
        update: (node) => node.hasAttribute(`data-${PLUGIN_KEY}`) && ''
      });

      on('keydown', (e) => {
        const currentNode = im.value.getSelectedNode();

        if (currentNode.hasAttribute(`data-${PLUGIN_KEY}`) && !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
          e.preventDefault();
        }
      });
    });
};

const handleSubmit = (data: { label: string, url: string }) => {
  const content = getContent(data.label, data.url, PLUGIN_KEY);

  if (modalMode.value === ModalMode.Edit) {
    im.value.replaceContent(content);
  } else {
    im.value.insertContent(content);
    emit('contentInserted', PLUGIN_KEY);
  }

  if (!im.value.queryCommandState('JustifyCenter')) {
    im.value.justifyCenter();
  }

  closeModal();
};

const openModal = async (options: { mode?: ModalMode, label?: string, url?: string }) => {
  modalMode.value = options.mode ?? ModalMode.Insert;
  modal.value.open(options);
};

const closeModal = () => {
  modalMode.value = ModalMode.Closed;
  modal.value.close();
};

watchEffect(() => {
  if (modal.value && im.value && pluginId.value) {
    register();
  }
});

const modalCopy = computed(() => ({
  title: t('account.brands.createCampaigns.editor.toolbar.insertCta.modal.title')  ,
  description: t('account.brands.createCampaigns.editor.toolbar.insertCta.modal.description'),
  labelPlaceholder: t('account.brands.createCampaigns.editor.toolbar.insertCta.modal.label.placeholder'),
  labelLabel: t('account.brands.createCampaigns.editor.toolbar.insertCta.modal.label.label'),
  urlPlaceholder: t('account.brands.createCampaigns.editor.toolbar.insertCta.modal.url.placeholder'),
  urlLabel: t('account.brands.createCampaigns.editor.toolbar.insertCta.modal.url.label'),
  cancel: t('account.brands.createCampaigns.editor.toolbar.insertCta.modal.cancel'),
  submit: t('account.brands.createCampaigns.editor.toolbar.insertCta.modal.submit'),
  submitUpdate: t('account.brands.createCampaigns.editor.toolbar.insertCta.modal.submitUpdate'),
  errorRequired: t('account.brands.createCampaigns.editor.toolbar.insertCta.modal.error.required'),
  errorWrongPrefix: t('account.brands.createCampaigns.editor.toolbar.insertCta.modal.url.error.wrongPrefix'),
  errorInvalidDomain: t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.url.error.invalidDomain'),
}));

</script>
<template>
  <LinkEditorModal
    ref="modal"
    :copy="modalCopy"
    @submit="handleSubmit"
    @cancel="closeModal"
  />
</template>
